<template>
  <div class="panel">
    <header class="page-header">
      <div>
        <h1>{{ title }}</h1>
        <p>{{ currentStore?.name || '鲸夕总店' }} · 共 {{ materialTotalCount }} 个素材</p>
      </div>
      <div class="actions">
        <button v-if="!isAllStores && !selectedFolderId" type="button" class="btn" @click="onCreateFolder">新建文件夹</button>
        <button type="button" class="btn-primary" @click="triggerUpload">上传素材</button>
      </div>
    </header>

    <div class="info-banner">
      <div class="info-banner-left">
        <svg class="info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span class="info-banner-copy">
          {{ canManage
            ? (selectedFolderId
              ? '勾选素材可批量移出文件夹；也可以直接拖动移回素材库。设计时可在编辑器左侧素材库替换 Logo / 二维码。'
              : '勾选素材可批量移入文件夹；也可以直接拖动归类。设计时可在编辑器左侧素材库替换 Logo / 二维码。')
            : '点击素材可查看预览；设计时可在编辑器左侧素材库替换 Logo / 二维码。' }}
        </span>
      </div>

      <!-- 批量操作栏组件 -->
      <MaterialBatchBar
        :selected-count="selectedIds.size"
        :folders="folders"
        :selected-folder-id="selectedFolderId"
        :can-batch-delete="canBatchDelete"
        @move-to="requestMoveTo"
        @move-out="requestMoveOut"
        @delete="deleteSelected"
        @clear="clearSelection"
      />
    </div>

    <!-- 搜索与筛选工具栏 -->
    <div class="material-toolbar">
      <div class="search-wrap">
        <svg class="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input v-model.trim="keyword" type="search" placeholder="搜索素材名称" aria-label="搜索素材名称" />
      </div>
      <select v-model="categoryFilter" aria-label="筛选素材类型">
        <option value="all">全部类型</option>
        <option value="logo">Logo</option>
        <option value="qrcode">二维码</option>
        <option value="upload">其他图片</option>
      </select>
      <select v-model="sortBy" aria-label="素材排序">
        <option value="newest">最新上传</option>
        <option value="name">名称排序</option>
        <option value="category">类型排序</option>
      </select>
      <span class="toolbar-count">共 {{ visibleMaterials.length }} 项</span>
    </div>

    <!-- 面包屑导航 -->
    <div v-if="selectedFolderId" class="mat-breadcrumb">
      <button
        type="button"
        class="btn btn-sm mat-breadcrumb-back"
        :class="{ 'is-drop-target': dropTarget === '__back__' }"
        @click="selectedFolderId = null"
        @dragover.prevent="onBackDragOver"
        @dragleave="onBackDragLeave"
        @drop.prevent="onDropToBack"
      >← 返回素材库</button>
      <span class="mat-breadcrumb-sep">/</span>
      <strong class="mat-breadcrumb-name" :title="currentFolder?.name || ''">{{ currentFolder?.name || '' }}</strong>
      <button type="button" class="btn btn-sm danger-outline mat-breadcrumb-delete" @click="onDeleteFolder">删除文件夹</button>
    </div>

    <!-- 素材卡片网格 -->
    <div v-if="!showEmptyState" class="material-grid">
      <label class="upload-tile" @click.prevent="triggerUpload">
        <span class="plus">+</span>
        <span>上传素材</span>
      </label>

      <!-- 文件夹卡片列表 -->
      <template v-if="!selectedFolderId && !isFiltering">
        <MaterialFolderCard
          v-for="folder in folders"
          :key="folder.id"
          :folder="folder"
          :can-manage="canManage"
          :count="folderMaterialCount(folder.id)"
          :is-drop-target="dropTarget === folder.id"
          @click="onFolderClick"
          @rename="onRenameFolder"
          @delete="onDeleteFolder"
          @dragover="onFolderDragOver"
          @dragleave="onFolderDragLeave"
          @drop="onDropToFolder(folder, $event)"
        />
      </template>

      <!-- 素材文件卡片列表 -->
      <MaterialCard
        v-for="m in visibleMaterials"
        :key="m.id"
        :material="m"
        :can-manage="canManage"
        :can-delete="canDeleteMaterialItem(m)"
        :is-selected="selectedIds.has(m.id)"
        :in-folder="!!selectedFolderId"
        :is-all-stores="isAllStores"
        :is-filtering="isFiltering"
        :store-name="storeName(m.storeId)"
        :folder-name="folderName(m.folderId)"
        :cat-label="categoryLabel(m.category)"
        :image-src="materialSrc(m)"
        @click="onMaterialClick"
        @toggle-select="onToggleSelection"
        @move-out="onMoveOutMaterial"
        @rename="onRenameMaterial"
        @delete="onDeleteMaterial"
        @dragstart="onDragStart"
        @dragend="onDragEnd"
        @img-error="onMaterialImgError"
      />
    </div>

    <!-- 空状态提示 -->
    <WhaleEmpty
      v-if="showEmptyState"
      size="large"
      class="materials-empty"
      :variant="isFiltering ? 'search' : 'materials'"
      :title="isFiltering ? '无匹配素材' : '暂无素材'"
      :description="emptyDescription"
      :action-text="canManage && !isFiltering ? '上传素材' : (isFiltering ? '重置筛选' : '')"
      @action="onEmptyAction"
    />

    <!-- 素材大图预览弹窗 -->
    <MaterialPreviewModal
      :model-value="!!previewMaterial"
      :material="previewMaterial"
      :image-src="materialSrc(previewMaterial)"
      :cat-label="categoryLabel(previewMaterial?.category)"
      @update:model-value="previewMaterial = null"
      @close="closePreview"
    />

    <!-- 系统通用交互确认弹窗 -->
    <SystemDialog
      :open="dialog.open"
      :title="dialog.title"
      :message="dialog.message"
      :confirm-text="dialog.confirmText"
      :danger="dialog.danger"
      :input="dialog.input"
      :input-value="dialog.inputValue"
      :error-message="dialog.errorMessage"
      :input-label="dialog.inputLabel"
      :input-placeholder="dialog.inputPlaceholder"
      @update:input-value="onDialogInput"
      @cancel="closeDialog"
      @confirm="confirmDialog"
    />

    <!-- 上传素材弹窗 -->
    <UploadDialog
      :open="uploadDialog.open"
      title="上传素材"
      description="可从本机选择一个或多个图片文件；批量上传时使用各自文件名。"
      name-label="素材名称"
      name-placeholder="例如：品牌 Logo"
      :name-value="uploadDialog.name"
      :file="uploadDialog.file"
      :files="uploadDialog.files"
      multiple
      :category-value="uploadDialog.category"
      :category-options="uploadCategoryOptions"
      accept="image/*"
      file-hint="支持多选 PNG、JPG、WEBP、SVG 等图片格式"
      :loading="uploadDialog.loading"
      :loading-message="uploadDialog.loadingMessage"
      :error-message="uploadDialog.error"
      @update:name-value="onUploadNameInput"
      @update:file="uploadDialog.file = $event"
      @update:files="uploadDialog.files = $event"
      @update:category-value="uploadDialog.category = $event"
      @cancel="closeUploadDialog"
      @confirm="confirmUpload"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import SystemDialog from '@/components/platform/SystemDialog.vue';
import UploadDialog from '@/components/platform/UploadDialog.vue';
import MaterialCard from '@/components/platform/materials/MaterialCard.vue';
import MaterialFolderCard from '@/components/platform/materials/MaterialFolderCard.vue';
import MaterialPreviewModal from '@/components/platform/materials/MaterialPreviewModal.vue';
import MaterialBatchBar from '@/components/platform/materials/MaterialBatchBar.vue';
import WhaleEmpty from '@/components/common/WhaleEmpty.vue';
import { resolveCdpAsset } from '@/utils/platformHelpers';
import { normalizeMaterialFolderId } from '@/utils/artMapper';
import { isUnitTestRuntime } from '@/utils/runtimeEnv';

const props = defineProps({
  title: { type: String, default: '素材库' },
  querySource: { type: Boolean, default: false },
});
const title = computed(() => props.title);
const apiModule = computed(() => props.querySource ? 'queryMaterials' : 'materials');

const {
  currentStore,
  session,
  platform,
  storeMaterials,
  storeMaterialTotalCount,
  storeMaterialFolders,
  canManageMaterials,
  canDeleteMaterial,
  createMaterialFolder,
  renameMaterialFolder,
  deleteMaterialFolder,
  folderMaterialCount,
  deleteMaterial,
  batchDeleteMaterials,
  renameMaterial,
  moveMaterialToFolder,
  batchMoveMaterials,
  uploadMaterial,
  showToast,
  artApiStatus,
  artModuleLoading,
  refreshModuleApi,
} = usePlatformStore();

function materialSrc(m) {
  const url = String(m?.thumb || m?.url || '').trim();
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return resolveCdpAsset(url);
}

function onMaterialImgError(event) {
  const img = event?.target;
  if (!img) return;
  img.style.display = 'none';
  const fallback = img.parentElement?.querySelector?.('.material-fallback-dynamic');
  if (fallback) return;
  const el = document.createElement('div');
  el.className = 'material-fallback material-fallback-dynamic';
  el.textContent = '无预览';
  img.insertAdjacentElement('afterend', el);
}

const materials = computed(() => storeMaterials());
const materialTotalCount = computed(() => storeMaterialTotalCount());
const canManage = computed(() => canManageMaterials());
const canBatchDelete = computed(() => Array.from(selectedIds.value).some((id) => {
  const material = materials.value.find(item => item.id === id);
  return canDeleteMaterialItem(material);
}));
const isAllStores = computed(() => session.storeId === 'all');
const folders = computed(() => [...storeMaterialFolders()]);
const selectedFolderId = ref(null);
const keyword = ref('');
const categoryFilter = ref('all');
const sortBy = ref('newest');
const selectedIds = ref(new Set());
const dropTarget = ref(null);
const suppressFolderClick = ref(false);
const suppressMaterialClick = ref(false);
const draggingId = ref(null);
const previewMaterial = ref(null);
const uploadDialog = ref({
  open: false,
  name: '',
  category: 'upload',
  file: null,
  files: [],
  loading: false,
  loadingMessage: '',
  error: '',
});
const uploadCategoryOptions = [
  { value: 'logo', label: 'Logo' },
  { value: 'qrcode', label: '二维码' },
  { value: 'upload', label: '其他图片' },
];
const dialog = ref({
  open: false,
  type: '',
  title: '',
  message: '',
  confirmText: '确认',
  danger: false,
  input: false,
  inputValue: '',
  inputLabel: '名称',
  inputPlaceholder: '',
  errorMessage: '',
  target: null,
});

const isFiltering = computed(() => !!keyword.value.trim() || categoryFilter.value !== 'all');

const showEmptyState = computed(() => {
  if (selectedFolderId.value || isFiltering.value) {
    return visibleMaterials.value.length === 0;
  }
  return !folders.value.length && visibleMaterials.value.length === 0;
});

const emptyDescription = computed(() => {
  if (isFiltering.value) return '没有符合当前筛选条件的素材';
  if (!canManage.value) return '当前暂无可用素材';
  if (selectedFolderId.value) return '当前文件夹暂无素材，可上传图片';
  return '当前店铺/分类下暂无素材，可上传图片或新建文件夹';
});

const currentFolder = computed(() => folders.value.find(f => f.id === selectedFolderId.value) || null);

function normalizeSearchText(text) {
  return String(text || '').toLocaleLowerCase().replace(/\s+/g, '');
}

const visibleMaterials = computed(() => {
  let list = materials.value;
  const filtering = isFiltering.value;

  if (!filtering) {
    if (selectedFolderId.value) {
      list = list.filter(m => normalizeMaterialFolderId(m.folderId) === selectedFolderId.value);
    } else {
      list = list.filter(m => !normalizeMaterialFolderId(m.folderId));
    }
  } else if (selectedFolderId.value) {
    list = list.filter(m => normalizeMaterialFolderId(m.folderId) === selectedFolderId.value);
  }

  const query = keyword.value.trim();
  if (query) {
    const normalizedQuery = normalizeSearchText(query);
    list = list.filter(m => normalizeSearchText(m.name).includes(normalizedQuery));
  }
  if (categoryFilter.value !== 'all') {
    list = list.filter(m => normalizeCategory(m.category) === categoryFilter.value);
  }
  const sorted = [...list];
  if (sortBy.value === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
  if (sortBy.value === 'category') {
    sorted.sort((a, b) => categoryLabel(a.category).localeCompare(categoryLabel(b.category), 'zh-CN'));
  }
  return sorted;
});

watch(() => session.storeId, () => {
  selectedFolderId.value = null;
  foldersExpanded.value = false;
  clearSelection();
});

function onEmptyAction() {
  if (isFiltering.value) {
    keyword.value = '';
    categoryFilter.value = 'all';
  } else if (canManage.value) {
    triggerUpload();
  }
}

function normalizeCategory(cat) {
  return cat === 'qr' ? 'qrcode' : (cat || 'upload');
}

function categoryLabel(cat) {
  const map = {
    logo: 'Logo',
    qrcode: '二维码',
    qr: '二维码',
    upload: '上传',
    image: '图片',
  };
  return map[cat] || cat || '素材';
}

function storeName(storeId) {
  if (!storeId || storeId === 'all') return '平台通用';
  const cleanId = String(storeId).replace(/^s/i, '');
  const match = platform.stores.find(store =>
    store.id === storeId
    || store.id === `s${cleanId}`
    || String(store.departmentId) === cleanId
    || String(store.id) === cleanId
  );
  if (match?.name || match?.departmentName) return match.name || match.departmentName;
  if (cleanId === '1') return '鲸夕总店';
  return `店铺${cleanId}`;
}

function folderName(folderId) {
  return folders.value.find(folder => folder.id === folderId)?.name || '文件夹';
}

function onDialogInput(value) {
  dialog.value.inputValue = value;
  dialog.value.errorMessage = '';
}

function onUploadNameInput(value) {
  uploadDialog.value.name = value;
  uploadDialog.value.error = '';
}

function onCreateFolder() {
  dialog.value = {
    open: true,
    type: 'create-folder',
    title: '新建素材文件夹',
    message: '创建文件夹后，可以拖动素材进行分类管理。',
    confirmText: '创建',
    danger: false,
    input: true,
    inputValue: '新建文件夹',
    inputLabel: '文件夹名称',
    inputPlaceholder: '例如：品牌 Logo',
    errorMessage: '',
    target: null,
  };
}

function onRenameFolder(folder) {
  if (!folder) return;
  dialog.value = {
    open: true,
    type: 'rename-folder',
    title: '重命名素材文件夹',
    message: '修改文件夹名称不会影响其内部素材。',
    confirmText: '保存',
    danger: false,
    input: true,
    inputValue: folder.name,
    inputLabel: '文件夹名称',
    inputPlaceholder: '例如：品牌 Logo',
    errorMessage: '',
    target: folder,
  };
}

async function onMoveOutMaterial(m) {
  if (!m) return;
  const ok = await moveMaterialToFolder(m.id, null);
  if (ok) {
    showToast(`已将素材「${m.name}」移出文件夹`, 'success');
  }
}

function onDeleteFolder(targetFolder = currentFolder.value) {
  const folder = targetFolder;
  if (!folder) return;
  openDeleteDialog(
    'delete-folder',
    '删除素材文件夹',
    `确定删除「${folder.name}」吗？文件夹内的素材会移回素材库，不会被删除。`,
    folder,
  );
}

function onRenameMaterial(material) {
  dialog.value = {
    open: true,
    type: 'rename-material',
    title: '重命名素材',
    message: '素材名称会同步显示在编辑器素材抽屉中。',
    confirmText: '保存',
    danger: false,
    input: true,
    inputValue: material.name,
    inputLabel: '素材名称',
    inputPlaceholder: '例如：品牌 Logo',
    errorMessage: '',
    target: material,
  };
}

function canDeleteMaterialItem(material) {
  return canDeleteMaterial(material);
}

function onDeleteMaterial(m) {
  if (!m || !canDeleteMaterialItem(m)) {
    showToast('当前角色无删除该素材权限', 'warn');
    return;
  }
  openDeleteDialog(
    'delete-material',
    '删除素材',
    `确定删除「${m.name}」吗？删除后将无法在设计中继续使用。`,
    m,
  );
}

function openDeleteDialog(type, title, message, target) {
  dialog.value = {
    open: true,
    type,
    title,
    message,
    confirmText: '确认删除',
    danger: true,
    input: false,
    inputValue: '',
    inputLabel: '名称',
    inputPlaceholder: '',
    errorMessage: '',
    target,
  };
}

function closeDialog() {
  dialog.value.open = false;
}

async function confirmDialog() {
  const action = dialog.value;
  if (action.type === 'create-folder') {
    const name = action.inputValue.trim();
    if (!name) {
      dialog.value.errorMessage = '请输入文件夹名称';
      return;
    }
    if (storeMaterialFolders().some(folder => folder.name === name)) {
      dialog.value.errorMessage = '已有同名文件夹，请换一个名称';
      return;
    }
    const created = await createMaterialFolder(name);
    if (!created) {
      dialog.value.errorMessage = '创建失败，请检查文件夹名称或稍后重试';
      return;
    }
  } else if (action.type === 'rename-folder' && action.target) {
    const name = action.inputValue.trim();
    if (!name) {
      dialog.value.errorMessage = '请输入文件夹名称';
      return;
    }
    if (storeMaterialFolders().some(folder => folder.id !== action.target.id && folder.name === name)) {
      dialog.value.errorMessage = '已有同名文件夹，请换一个名称';
      return;
    }
    const ok = await renameMaterialFolder(action.target.id, name);
    if (!ok) {
      dialog.value.errorMessage = '重命名失败，请稍后重试';
      return;
    }
  } else if (action.type === 'delete-folder' && action.target) {
    await deleteMaterialFolder(action.target.id);
    if (selectedFolderId.value === action.target.id) {
      selectedFolderId.value = null;
    }
  } else if (action.type === 'rename-material' && action.target) {
    const name = action.inputValue.trim();
    if (!name) {
      dialog.value.errorMessage = '请输入素材名称';
      return;
    }
    const ok = await renameMaterial(action.target.id, name);
    if (!ok) {
      dialog.value.errorMessage = '重命名失败，请稍后重试';
      return;
    }
  } else if (action.type === 'delete-material' && action.target) {
    await deleteMaterial(action.target.id);
    selectedIds.value.delete(action.target.id);
  } else if (action.type === 'batch-delete-materials' && action.target?.ids?.length) {
    const ok = await batchDeleteMaterials(action.target.ids);
    if (ok) {
      clearSelection();
    }
  }
  closeDialog();
}

function onToggleSelection(id, event) {
  if (event?.shiftKey && selectedIds.value.size) {
    const ids = visibleMaterials.value.map(m => m.id);
    const lastId = Array.from(selectedIds.value).pop();
    const start = ids.indexOf(lastId);
    const end = ids.indexOf(id);
    if (start !== -1 && end !== -1) {
      const [from, to] = [Math.min(start, end), Math.max(start, end)];
      for (let i = from; i <= to; i++) {
        selectedIds.value.add(ids[i]);
      }
      return;
    }
  }
  if (selectedIds.value.has(id)) {
    selectedIds.value.delete(id);
  } else {
    selectedIds.value.add(id);
  }
}

function clearSelection() {
  selectedIds.value.clear();
}

async function requestMoveTo(targetFolderId) {
  const ids = Array.from(selectedIds.value);
  if (!ids.length) return;
  const ok = await batchMoveMaterials(ids, targetFolderId);
  if (ok) {
    clearSelection();
    showToast(`已将 ${ids.length} 个素材移入文件夹`, 'success');
  }
}

async function requestMoveOut() {
  const ids = Array.from(selectedIds.value);
  if (!ids.length) return;
  const ok = await batchMoveMaterials(ids, null);
  if (ok) {
    clearSelection();
    showToast(`已将 ${ids.length} 个素材移回素材库`, 'success');
  }
}

async function deleteSelected() {
  const ids = Array.from(selectedIds.value);
  if (!ids.length) return;
  openDeleteDialog(
    'batch-delete-materials',
    '批量删除素材',
    `确定删除选中的 ${ids.length} 个素材吗？删除后将无法在设计中继续使用。`,
    { ids },
  );
}

function onFolderClick(folderId) {
  if (suppressFolderClick.value) {
    suppressFolderClick.value = false;
    return;
  }
  selectedFolderId.value = folderId;
  clearSelection();
}

function onMaterialClick(m) {
  if (suppressMaterialClick.value) {
    suppressMaterialClick.value = false;
    return;
  }
  previewMaterial.value = m;
}

function closePreview() {
  previewMaterial.value = null;
}

function onDragStart(m, event) {
  draggingId.value = m.id;
  if (!selectedIds.value.has(m.id)) {
    selectedIds.value.clear();
    selectedIds.value.add(m.id);
  }
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/material-id', String(m.id));
  event.dataTransfer.setData('text/plain', JSON.stringify({
    type: 'material-batch',
    ids: Array.from(selectedIds.value),
  }));
}

function onDragEnd() {
  draggingId.value = null;
  dropTarget.value = null;
  suppressMaterialClick.value = true;
  suppressFolderClick.value = true;
  setTimeout(() => {
    suppressMaterialClick.value = false;
    suppressFolderClick.value = false;
  }, 400);
}

function onFolderDragOver(folderId, event) {
  if (!draggingId.value) return;
  event.dataTransfer.dropEffect = 'move';
  dropTarget.value = folderId;
}

function onFolderDragLeave(folderId) {
  if (dropTarget.value === folderId) dropTarget.value = null;
}

async function onDropToFolder(folder, event) {
  const actualFolder = (folder && ('name' in folder || 'id' in folder) && !('dataTransfer' in folder))
    ? folder
    : (event && ('name' in event || 'id' in event) && !('dataTransfer' in event) ? event : null);
  const actualEvent = (event && 'dataTransfer' in event)
    ? event
    : (folder && 'dataTransfer' in folder ? folder : null);

  actualEvent?.preventDefault?.();
  actualEvent?.stopPropagation?.();
  dropTarget.value = null;
  suppressFolderClick.value = true;
  setTimeout(() => { suppressFolderClick.value = false; }, 400);
  if (!actualFolder) return;

  const raw = actualEvent?.dataTransfer?.getData?.('text/plain') || '';
  if (raw) {
    try {
      const data = JSON.parse(raw);
      if (data.type === 'material-batch' && Array.isArray(data.ids) && data.ids.length) {
        const ok = await batchMoveMaterials(data.ids, actualFolder.id);
        if (ok) {
          showToast(`已将 ${data.ids.length} 个素材移入「${actualFolder.name}」`, 'success');
        }
        clearSelection();
        return;
      }
    } catch {
      /* 非 JSON，按单个素材 ID 处理 */
    }
  }

  const id = actualEvent?.dataTransfer?.getData?.('text/material-id') || raw || draggingId.value;
  if (!id || String(id).startsWith('{')) return;
  const m = materials.value.find(x => String(x.id) === String(id));
  if (await moveMaterialToFolder(id, actualFolder.id)) {
    showToast(`已将「${m?.name || '素材'}」移入「${actualFolder.name}」`, 'success');
    clearSelection();
  }
}

function onBackDragOver(event) {
  if (!draggingId.value) return;
  event.dataTransfer.dropEffect = 'move';
  dropTarget.value = '__back__';
}

function onBackDragLeave() {
  if (dropTarget.value === '__back__') {
    dropTarget.value = null;
  }
}

async function onDropToBack(e) {
  e.preventDefault();
  dropTarget.value = null;

  const raw = e.dataTransfer.getData('text/plain');
  if (raw) {
    try {
      const data = JSON.parse(raw);
      if (data.type === 'material-batch' && Array.isArray(data.ids) && data.ids.length) {
        const ok = await batchMoveMaterials(data.ids, null);
        if (ok) {
          showToast(`已将 ${data.ids.length} 个素材移回素材库`, 'success');
        }
        clearSelection();
        return;
      }
    } catch {
      /* 非 JSON，按单个素材 ID 处理 */
    }
  }

  const id = e.dataTransfer.getData('text/material-id') || raw || draggingId.value;
  if (!id || String(id).startsWith('{')) return;
  const m = materials.value.find(x => String(x.id) === String(id));
  if (await moveMaterialToFolder(id, null)) {
    showToast(`已将「${m?.name || '素材'}」移出文件夹`, 'success');
    clearSelection();
  }
}

function triggerUpload() {
  uploadDialog.value = {
    open: true,
    name: '',
    category: 'upload',
    file: null,
    files: [],
    loading: false,
    loadingMessage: '',
    error: '',
  };
}

function closeUploadDialog() {
  uploadDialog.value.open = false;
}

async function confirmUpload() {
  const { name, category, file } = uploadDialog.value;
  const selectedFiles = uploadDialog.value.files?.length
    ? uploadDialog.value.files.filter(Boolean)
    : (file ? [file] : []);
  if (!selectedFiles.length) {
    uploadDialog.value.error = '请选择要上传的文件';
    return;
  }
  const isBatch = selectedFiles.length > 1;
  const cleanName = isBatch ? '' : (name || selectedFiles[0].name.replace(/\.[^.]+$/, '')).trim();
  if (!isBatch && !cleanName) {
    uploadDialog.value.error = '请输入素材名称';
    return;
  }
  uploadDialog.value.loading = true;
  uploadDialog.value.loadingMessage = '';
  uploadDialog.value.error = '';
  const uploaded = [];
  const failed = [];
  try {
    const folderId = normalizeMaterialFolderId(selectedFolderId.value) || 0;
    for (let index = 0; index < selectedFiles.length; index += 1) {
      const currentFile = selectedFiles[index];
      const materialName = isBatch
        ? currentFile.name.replace(/\.[^.]+$/, '').trim()
        : cleanName;
      uploadDialog.value.loadingMessage = isBatch
        ? `正在上传 ${index + 1}/${selectedFiles.length}：${currentFile.name}`
        : `正在上传：${currentFile.name}`;
      try {
        const result = await uploadMaterial(currentFile, {
          name: materialName,
          category,
          folderId,
        });
        if (result?.error) {
          failed.push({ file: currentFile, reason: result.error });
        } else if (result) {
          uploaded.push(result);
        } else {
          failed.push({ file: currentFile, reason: '文件格式、大小或权限不符合要求' });
        }
      } catch (err) {
        failed.push({ file: currentFile, reason: err?.message || '上传异常' });
      }
    }

    if (!failed.length) {
      closeUploadDialog();
      if (isBatch) showToast(`已成功上传 ${uploaded.length} 个素材`, 'success');
    } else {
      uploadDialog.value.files = failed.map(item => item.file);
      uploadDialog.value.file = failed[0]?.file || null;
      uploadDialog.value.name = failed.length === 1
        ? failed[0].file.name.replace(/\.[^.]+$/, '')
        : '';
      const details = failed.slice(0, 3).map(item => `${item.file.name}（${item.reason}）`).join('、');
      const prefix = uploaded.length ? `已成功 ${uploaded.length} 个，` : '';
      uploadDialog.value.error = `${prefix}失败 ${failed.length} 个：${details}${failed.length > 3 ? '等' : ''}`;
    }
  } finally {
    uploadDialog.value.loading = false;
    uploadDialog.value.loadingMessage = '';
  }
}

onMounted(async () => {
  if (!isUnitTestRuntime()) {
    try {
      await refreshModuleApi?.(props.querySource ? 'materials' : 'materials');
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[MaterialsPanel] 初始化加载素材/文件夹失败', err);
    }
  }
});
</script>

<style scoped>
.panel {
  padding: 24px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.page-header h1 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
  color: var(--text-dark, #0f172a);
}
.page-header p {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted, #64748b);
}
.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn {
  padding: 8px 16px;
  border-radius: var(--radius-round, 9999px);
  border: 1px solid var(--border-color, #e2e8f0);
  background: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}
.btn-primary {
  padding: 8px 16px;
  border-radius: var(--radius-round, 9999px);
  background: #2c2c2c;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn-primary:hover {
  background: #1c1c1e;
}
.btn-sm {
  padding: 4px 10px;
  border-radius: var(--radius-round, 9999px);
  border: 1px solid var(--border-color, #e2e8f0);
  background: #ffffff;
  font-size: 12px;
  cursor: pointer;
}
.danger-outline {
  margin-left: auto;
  color: #cf1322;
  border-color: #ffa39e;
}
.danger-outline:hover {
  background: #fff1f0;
}

.info-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #f5f5f5;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  font-size: 12px;
  color: #595959;
  flex-wrap: wrap;
}
.info-banner-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.info-icon {
  flex-shrink: 0;
  color: #8c8c8c;
}
.info-banner-copy {
  line-height: 1.45;
  color: #595959;
}
.info-banner .batch-actions strong {
  color: #434343;
}

.material-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: min(280px, 40vw);
}
.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  pointer-events: none;
}
.search-wrap input {
  width: 100%;
  height: 36px;
  padding: 0 12px 0 32px;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.search-wrap input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.12);
}
.material-toolbar select {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.material-toolbar select:focus {
  border-color: var(--color-primary);
}
.toolbar-count {
  margin-left: auto;
  color: var(--text-muted, #64748b);
  font-size: 13px;
}

.mat-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  min-width: 0;
  font-size: 13px;
  color: var(--text-muted, #64748b);
}
.mat-breadcrumb-back {
  flex-shrink: 0;
  white-space: nowrap;
}
.mat-breadcrumb-sep {
  flex-shrink: 0;
  opacity: 0.5;
}
.mat-breadcrumb-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-dark, #0f172a);
  font-weight: 600;
}
.mat-breadcrumb-delete {
  flex-shrink: 0;
  margin-left: auto;
  white-space: nowrap;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.folder-toggle-card {
  min-height: 180px;
  border: 1px dashed var(--border-color, #cbd5e1);
  border-radius: 12px;
  background: #ffffff;
  color: var(--text-muted, #64748b);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.folder-toggle-card:hover {
  border-color: #7c3aed;
  color: #6d28d9;
  background: #f5f3ff;
}

.upload-tile {
  min-height: 180px;
  border-radius: 12px;
  border: 2px dashed var(--border-color, #cbd5e1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  background: #fafafa;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted, #64748b);
  transition: var(--transition-fast, all 0.15s ease);
  padding: 16px;
}
.upload-tile:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: var(--color-primary-light);
}
.plus {
  font-size: 32px;
  font-weight: 300;
  line-height: 1;
}

.btn-sm.is-drop-target {
  border-color: var(--color-primary);
  background: var(--color-primary-light);
  color: #5b21b6;
}

.mat-empty {
  margin-top: 32px;
  text-align: center;
  color: var(--text-muted, #64748b);
  font-size: 14px;
}

.materials-empty {
  margin-top: 24px;
  min-height: 320px;
}
</style>
